<template>
  <Teleport to="body">
    <transition name="modal">
      <div v-if="visible" class="announcement-modal-overlay" @click="handleOverlayClick">
    <div class="announcement-modal" @click.stop>
      <div class="announcement-header">
        <h2>公告</h2>
        <button class="close-btn" @click="closeModal">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      
      <!-- 选项卡导航 -->
      <div class="tab-navigation">
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'current' }"
          @click="switchTab('current')"
        >
          最新公告
        </button>
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'history' }"
          @click="switchTab('history')"
        >
          历史公告
        </button>
      </div>
      
      <div class="announcement-content">
        <!-- 当前公告内容 -->
        <transition name="tab-fade" mode="out-in">
          <div v-if="activeTab === 'current'" key="current" class="tab-content">
            <div v-if="loading" class="loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              加载中...
            </div>
            <div v-else-if="error" class="error">
              {{ error }}
            </div>
            <div v-else class="announcement-text">
              <div v-if="announcement.title" class="announcement-title">
                {{ announcement.title }}
              </div>
              <div class="announcement-body" v-html="renderMarkdown(announcement.content)"></div>
              <div v-if="announcement.publishTime || announcement.date" class="announcement-date">
                发布时间：{{ formatDateDisplay(announcement.publishTime || announcement.date) }}
              </div>
            </div>
          </div>
          
          <!-- 历史公告内容 -->
          <div v-else-if="activeTab === 'history'" key="history" class="tab-content">
            <div v-if="historyLoading" class="loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              加载历史公告中...
            </div>
            <div v-else-if="historyError" class="error">
              {{ historyError }}
            </div>
            <div v-else-if="historyAnnouncements.length === 0" class="no-history">
              <i class="fa-solid fa-inbox"></i>
              <p>暂无历史公告</p>
            </div>
            <div v-else class="history-list">
              <div 
                v-for="(item, index) in historyAnnouncements" 
                :key="index"
                class="history-item"
                :class="{ expanded: expandedItems.includes(index) }"
              >
                <div class="history-header" @click="toggleExpand(index)">
                  <div class="history-title">{{ item.title }}</div>
                  <div class="history-meta">
                    <span class="history-date">{{ formatDateDisplay(item.publishTime || item.date) }}</span>
                    <i class="fa-solid fa-chevron-down expand-icon"></i>
                  </div>
                </div>
                <div v-if="expandedItems.includes(index)" class="history-content">
                  <div class="history-body" v-html="renderMarkdown(item.content)"></div>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import { marked } from 'marked'

export default {
  name: 'AnnouncementModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      announcement: {},
      loading: false,
      error: null,
      activeTab: 'current',
      historyAnnouncements: [],
      historyLoading: false,
      historyError: null,
      expandedItems: []
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.loadAnnouncement()
        // 重置状态
        this.activeTab = 'current'
        this.expandedItems = []
      }
    }
  },
  methods: {
    async loadAnnouncement() {
      this.loading = true
      this.error = null
      try {
        const response = await axios.get(`${serverUrl}/api/announcement`)
        if (response.data.success) {
          this.announcement = response.data.data
        } else {
          this.error = '获取公告失败'
        }
      } catch (error) {
        console.error('获取公告失败:', error)
        this.error = '获取公告失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    formatContent(content) {
      return this.renderMarkdown(content)
    },
    renderMarkdown(text) {
      if (!text) return ''
      try {
        return marked.parse(text, { breaks: true, gfm: true })
      } catch (e) {
        return text.replace(/\n/g, '<br>')
      }
    },
    async switchTab(tab) {
      this.activeTab = tab
      if (tab === 'history' && this.historyAnnouncements.length === 0) {
        await this.loadHistoryAnnouncements()
      }
    },
    async loadHistoryAnnouncements() {
      this.historyLoading = true
      this.historyError = null
      try {
        const response = await axios.get(`${serverUrl}/api/announcement/history`)
        if (response.data.success) {
          this.historyAnnouncements = response.data.data
        } else {
          this.historyError = '获取历史公告失败'
        }
      } catch (error) {
        console.error('获取历史公告失败:', error)
        this.historyError = '获取历史公告失败，请稍后重试'
      } finally {
        this.historyLoading = false
      }
    },
    toggleExpand(index) {
      const expandedIndex = this.expandedItems.indexOf(index)
      if (expandedIndex > -1) {
        this.expandedItems.splice(expandedIndex, 1)
      } else {
        this.expandedItems.push(index)
      }
    },
    closeModal() {
      this.$emit('close')
    },
    handleOverlayClick() {
      this.closeModal()
    },
    formatDateDisplay(value) {
      if (!value) return ''
      const d = new Date(value)
      if (!isNaN(d.getTime())) {
        return d.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).replace(/\//g, '/').replace(/,/g, '')
      }
      return value
    },
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.announcement-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6); /* 稍微加深蒙层，使层级更清晰 */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--z-dialog); /* 确保在最前面 */
  /* 确保全屏覆盖，不受父容器偏移影响 */

}

.announcement-modal {
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(15px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 28px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  max-width: 650px;
  width: 90%;
  max-height: 85vh;
  overflow: hidden;
}

/* 进场与退场动画 */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active .announcement-modal,
.modal-leave-active .announcement-modal {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .announcement-modal {
  transform: scale(0.92) translateY(30px);
  opacity: 0;
}

.modal-leave-to .announcement-modal {
  transform: scale(0.96) translateY(15px);
  opacity: 0;
}

.modal-enter-to .announcement-modal,
.modal-leave-from .announcement-modal {
  transform: scale(1) translateY(0);
  opacity: 1;
}

.announcement-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.2);
}

.announcement-header h2 {
  margin: 0;
  font-size: 1.5em;
  color: #333;
  font-family: '爱点风雅黑', sans-serif;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2em;
  color: #444; /* 从 #666 加深 */
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #e9ecef;
  color: #333;
}

/* 选项卡导航样式 */
.tab-navigation {
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.1);
}

.tab-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: none;
  color: #444; /* 加深 */
  font-size: 1em;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  font-family: '爱点风雅黑', sans-serif;
  position: relative;
  overflow: hidden;
}

.tab-btn::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 3px;
  background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform: translateX(-50%);
}

.tab-btn:hover {
  background: #e9ecef;
  color: #333;
  transform: translateY(-1px);
}

.tab-btn:hover::before {
  width: 60%;
}

.tab-btn.active {
  color: #4CAF50;
  background: rgba(255, 255, 255, 0.4);
  font-weight: 700;
}

.tab-btn.active::before {
  width: 100%;
  left: 0;
  transform: translateX(0);
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(102, 126, 234, 0.02));
  pointer-events: none;
}

.announcement-content {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
  position: relative;
}

/* 选项卡内容容器 */
.tab-content {
  width: 100%;
}

/* 选项卡切换动画 */
.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.tab-fade-enter-from {
  opacity: 0;
  transform: translateX(30px) scale(0.95);
}

.tab-fade-leave-to {
  opacity: 0;
  transform: translateX(-30px) scale(0.95);
}

.tab-fade-enter-to,
.tab-fade-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}

.loading, .error {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-family: '猫啃网糖圆体', sans-serif;
}

.loading i {
  margin-right: 8px;
  color: #667eea;
}

.error {
  color: #e74c3c;
}

.announcement-title {
  font-size: 1.3em;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: '爱点风雅黑', sans-serif;
}

.announcement-body {
  line-height: 1.6;
  color: #555;
  font-size: 1.1em;
  margin-bottom: 20px;
  font-family: '猫啃网糖圆体', sans-serif;
}

.announcement-body :deep(h1),
.announcement-body :deep(h2),
.announcement-body :deep(h3),
.announcement-body :deep(h4),
.announcement-body :deep(h5),
.announcement-body :deep(h6) {
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.announcement-body :deep(p) {
  margin: 8px 0;
}

.announcement-body :deep(ul),
.announcement-body :deep(ol) {
  padding-left: 24px;
  margin: 8px 0;
}

.announcement-body :deep(li) {
  margin: 4px 0;
}

.announcement-body :deep(code) {
  background: rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.announcement-body :deep(pre) {
  background: rgba(0, 0, 0, 0.06);
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 12px 0;
}

.announcement-body :deep(pre code) {
  background: none;
  padding: 0;
}

.announcement-body :deep(blockquote) {
  border-left: 4px solid #667eea;
  padding: 8px 16px;
  margin: 12px 0;
  background: rgba(102, 126, 234, 0.05);
  color: #666;
}

.announcement-body :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 12px 0;
}

.announcement-body :deep(th),
.announcement-body :deep(td) {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.announcement-body :deep(th) {
  background: #f5f5f5;
  font-weight: bold;
}

.announcement-body :deep(a) {
  color: #667eea;
  text-decoration: none;
}

.announcement-body :deep(a:hover) {
  text-decoration: underline;
}

.announcement-body :deep(hr) {
  border: none;
  border-top: 1px solid #eee;
  margin: 16px 0;
}

.announcement-body :deep(img) {
  max-width: 100%;
  border-radius: 8px;
}

.announcement-date {
  font-size: 0.9em;
  color: #888;
  text-align: right;
  border-top: 1px solid #eee;
  padding-top: 12px;
  font-family: '爱点风雅黑', sans-serif;
}

/* 历史公告样式 */
.no-history {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-family: '猫啃网糖圆体', sans-serif;
}

.no-history i {
  font-size: 3em;
  margin-bottom: 16px;
  display: block;
  color: #ddd;
}

.no-history p {
  margin: 0;
  font-size: 1.1em;
}

.history-list {
  max-height: 50vh;
  overflow-y: auto;
}

.history-item {
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.history-item:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.history-header {
  padding: 16px 20px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
  transition: background 0.2s ease;
}

.history-header:hover {
  background: #f0f0f0;
}

.history-item.expanded .history-header {
  background: #667eea;
  color: white;
}

.history-title {
  font-weight: bold;
  font-size: 1.1em;
  font-family: '爱点风雅黑', sans-serif;
  flex: 1;
  margin-right: 16px;
}

.history-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-date {
  font-size: 0.9em;
  color: #666;
  font-family: '爱点风雅黑', sans-serif;
}

.history-item.expanded .history-date {
  color: rgba(255, 255, 255, 0.8);
}

.expand-icon {
  transition: transform 0.2s ease;
  color: #999;
}

.history-item.expanded .expand-icon {
  transform: rotate(180deg);
  color: white;
}

.history-content {
  padding: 20px;
  border-top: 1px solid #eee;
  background: white;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  to {
    opacity: 1;
    max-height: 500px;
    padding-top: 20px;
    padding-bottom: 20px;
  }
}

.history-body {
  line-height: 1.6;
  color: #555;
  font-size: 1em;
  font-family: '猫啃网糖圆体', sans-serif;
}

.history-body :deep(h1),
.history-body :deep(h2),
.history-body :deep(h3),
.history-body :deep(h4),
.history-body :deep(h5),
.history-body :deep(h6) {
  margin-top: 12px;
  margin-bottom: 6px;
  font-weight: bold;
  color: #333;
}

.history-body :deep(p) {
  margin: 6px 0;
}

.history-body :deep(ul),
.history-body :deep(ol) {
  padding-left: 20px;
  margin: 6px 0;
}

.history-body :deep(code) {
  background: rgba(0, 0, 0, 0.06);
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.9em;
}

.history-body :deep(pre) {
  background: rgba(0, 0, 0, 0.06);
  padding: 10px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

.history-body :deep(pre code) {
  background: none;
  padding: 0;
}

.history-body :deep(blockquote) {
  border-left: 3px solid #667eea;
  padding: 6px 12px;
  margin: 8px 0;
  background: rgba(102, 126, 234, 0.05);
  color: #666;
}

.history-body :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}

.history-body :deep(th),
.history-body :deep(td) {
  border: 1px solid #ddd;
  padding: 6px 10px;
  text-align: left;
}

.history-body :deep(a) {
  color: #667eea;
  text-decoration: none;
}

.history-body :deep(a:hover) {
  text-decoration: underline;
}

/* 公告内容使用全局滚动条样式 */

/* 夜间模式样式 */
body.dark-theme .announcement-modal {
  background: rgba(30, 30, 30, 0.8);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
  color: #f5f5f7;
}

body.dark-theme .announcement-header {
  background: rgba(0, 0, 0, 0.2);
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

body.dark-theme .announcement-header h2 {
  color: #fff;
}

body.dark-theme .close-btn {
  color: #ccc;
}

body.dark-theme .close-btn:hover {
  background: #555;
  color: #fff;
}

body.dark-theme .loading,
body.dark-theme .error {
  color: #ccc;
}

body.dark-theme .announcement-title {
  color: #fff;
}

body.dark-theme .announcement-body {
  color: #ddd;
}

body.dark-theme .announcement-body :deep(h1),
body.dark-theme .announcement-body :deep(h2),
body.dark-theme .announcement-body :deep(h3),
body.dark-theme .announcement-body :deep(h4),
body.dark-theme .announcement-body :deep(h5),
body.dark-theme .announcement-body :deep(h6) {
  color: #fff;
}

body.dark-theme .announcement-body :deep(code) {
  background: rgba(255, 255, 255, 0.1);
}

body.dark-theme .announcement-body :deep(pre) {
  background: rgba(255, 255, 255, 0.08);
}

body.dark-theme .announcement-body :deep(blockquote) {
  background: rgba(102, 126, 234, 0.1);
  border-left-color: #667eea;
  color: #bbb;
}

body.dark-theme .announcement-body :deep(table th) {
  background: rgba(255, 255, 255, 0.08);
}

body.dark-theme .announcement-body :deep(table th),
body.dark-theme .announcement-body :deep(table td) {
  border-color: #555;
}

body.dark-theme .announcement-body :deep(a) {
  color: #8b9cf7;
}

body.dark-theme .announcement-body :deep(hr) {
  border-top-color: #555;
}

body.dark-theme .announcement-date {
  color: #aaa;
  border-top-color: #555;
}

/* 夜间模式 - 选项卡样式 */
body.dark-theme .tab-navigation {
  background: rgba(0, 0, 0, 0.1);
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

body.dark-theme .tab-btn {
  color: #ccc;
}

body.dark-theme .tab-btn::before {
  background: #667eea;
}

body.dark-theme .tab-btn:hover {
  background: #555;
  color: #fff;
}

body.dark-theme .tab-btn.active {
  color: #4CAF50;
  background: rgba(0, 0, 0, 0.2);
}

body.dark-theme .tab-btn.active::after {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(102, 126, 234, 0.05));
}

/* 夜间模式 - 历史公告样式 */
body.dark-theme .no-history {
  color: #666;
}

body.dark-theme .no-history i {
  color: #555;
}

body.dark-theme .history-item {
  border-color: #555;
  background: #3a3a3a;
}

body.dark-theme .history-item:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

body.dark-theme .history-header {
  background: #444;
  color: #fff;
}

body.dark-theme .history-header:hover {
  background: #555;
}

body.dark-theme .history-item.expanded .history-header {
  background: #667eea;
}

body.dark-theme .history-title {
  color: #f5f5f7;
}

body.dark-theme .history-desc {
  color: rgba(255, 255, 255, 0.6);
}

body.dark-theme .history-date {
  color: rgba(255, 255, 255, 0.5);
}

body.dark-theme .expand-icon {
  color: #aaa;
}

body.dark-theme .history-content {
  background: #2d2d2d;
  border-top-color: #555;
}

body.dark-theme .history-body {
  color: #ddd;
}

body.dark-theme .history-body :deep(h1),
body.dark-theme .history-body :deep(h2),
body.dark-theme .history-body :deep(h3),
body.dark-theme .history-body :deep(h4),
body.dark-theme .history-body :deep(h5),
body.dark-theme .history-body :deep(h6) {
  color: #fff;
}

body.dark-theme .history-body :deep(code) {
  background: rgba(255, 255, 255, 0.1);
}

body.dark-theme .history-body :deep(pre) {
  background: rgba(255, 255, 255, 0.08);
}

body.dark-theme .history-body :deep(blockquote) {
  background: rgba(102, 126, 234, 0.1);
  border-left-color: #667eea;
  color: #bbb;
}

body.dark-theme .history-body :deep(table th) {
  background: rgba(255, 255, 255, 0.08);
}

body.dark-theme .history-body :deep(table th),
body.dark-theme .history-body :deep(table td) {
  border-color: #555;
}

body.dark-theme .history-body :deep(a) {
  color: #8b9cf7;
}
@media (max-width: 600px) {
  .history-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .history-title {
    margin-right: 0;
    margin-bottom: 4px;
    width: 100%;
  }
  
  .history-meta {
    width: 100%;
    justify-content: space-between;
  }
}


</style>